import { useSearchParams } from 'react-router-dom';
import { SortingState } from '@tanstack/react-table';
import { useDebounce } from 'react-use';
import {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react';

interface Pagination {
  pageIndex: number;
  pageSize: number;
}

interface UseTableSearchParamsOptions {
  /** Namespaces the query params (e.g. 'e_') so a drill-down page's table
      state doesn't collide with its parent's when links carry params over */
  prefix?: string;
  defaultPageSize?: number;
}

const DEFAULT_PAGE_SIZE = 25;

/**
 * Drop-in replacement for the useState pagination/sorting/search trio that
 * DataTable pages use, persisted in the query string so the state survives
 * navigating away and back.
 */
export const useTableSearchParams = ({
  prefix = '',
  defaultPageSize = DEFAULT_PAGE_SIZE,
}: UseTableSearchParamsOptions = {}) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const pageKey = `${prefix}page`;
  const pageSizeKey = `${prefix}page_size`;
  const searchKey = `${prefix}q`;
  const sortByKey = `${prefix}sort_by`;
  const sortOrderKey = `${prefix}sort_order`;

  const pagination = useMemo<Pagination>(() => {
    const page = Number(searchParams.get(pageKey));
    const pageSize = Number(searchParams.get(pageSizeKey));
    return {
      pageIndex: Number.isInteger(page) && page > 0 ? page - 1 : 0,
      pageSize:
        Number.isInteger(pageSize) && pageSize > 0 ? pageSize : defaultPageSize,
    };
  }, [searchParams, pageKey, pageSizeKey, defaultPageSize]);

  const sorting = useMemo<SortingState>(() => {
    const sortBy = searchParams.get(sortByKey);
    if (!sortBy) return [];
    return [{ id: sortBy, desc: searchParams.get(sortOrderKey) === 'desc' }];
  }, [searchParams, sortByKey, sortOrderKey]);

  const debouncedSearchInput = searchParams.get(searchKey) ?? '';
  const [searchInput, setSearchInput] = useState(debouncedSearchInput);

  // Resync the input when the param changes from the outside (browser
  // back/forward); our own debounced write makes this a no-op
  useEffect(() => {
    setSearchInput(debouncedSearchInput);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchInput]);

  const setParams = (mutate: (params: URLSearchParams) => void) => {
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        mutate(next);
        return next;
      },
      { replace: true },
    );
  };

  const writePagination = (params: URLSearchParams, next: Pagination) => {
    if (next.pageIndex > 0) params.set(pageKey, String(next.pageIndex + 1));
    else params.delete(pageKey);
    if (next.pageSize !== defaultPageSize)
      params.set(pageSizeKey, String(next.pageSize));
    else params.delete(pageSizeKey);
  };

  const setPagination: Dispatch<SetStateAction<Pagination>> = (updater) => {
    const next = typeof updater === 'function' ? updater(pagination) : updater;
    setParams((params) => writePagination(params, next));
  };

  const setSorting: Dispatch<SetStateAction<SortingState>> = (updater) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    setParams((params) => {
      if (next[0]) {
        params.set(sortByKey, next[0].id);
        params.set(sortOrderKey, next[0].desc ? 'desc' : 'asc');
      } else {
        params.delete(sortByKey);
        params.delete(sortOrderKey);
      }
      // A different order means the current page no longer shows the same rows
      writePagination(params, { ...pagination, pageIndex: 0 });
    });
  };

  useDebounce(
    () => {
      if (searchInput === debouncedSearchInput) return;
      setParams((params) => {
        if (searchInput) params.set(searchKey, searchInput);
        else params.delete(searchKey);
        writePagination(params, { ...pagination, pageIndex: 0 });
      });
    },
    300,
    [searchInput],
  );

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
  };

  return {
    pagination,
    setPagination,
    sorting,
    setSorting,
    searchInput,
    debouncedSearchInput,
    handleSearchChange,
  };
};
